import React from "react";
import { useTranslation } from "react-i18next";

export default function Helpgh() {
  const { t } = useTranslation("help");

  return (
    <div className="whx-guide">

      {/* Header */}
      <div className="guide-header">
        <h1>
          {t("helpgh.header.title")}
        </h1>
        <p>
          {t("helpgh.header.description", {
            defaultValue: "Utilisation de GitHub Actions, deployment_status, de l'intégration de l'API REST WhiteHaX et du chargement SARIF dans l'analyse de code GitHub."
          })}
        </p>
      </div>

      {/* Recommended Use */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.recommendedUse.title", {
            defaultValue: "Utilisation recommandée"
          })}
        </h2>

        <ol>
          <li>
            {t("helpgh.sections.recommendedUse.item1", {
              defaultValue: "Considérez le flux de travail et la passerelle SARIF comme une structure réutilisable. Ne remplissez l'adaptateur d'API REST WhiteHaX qu'une fois que les détails exacts du point de terminaison de la plateforme sont disponibles."
            })}
          </li>

          <li>
            {t("helpgh.sections.recommendedUse.item2", {
              defaultValue: "Les noms de fichiers, chemins d'accès et noms de variables figurant dans ce guide ne sont que des exemples. Vous pouvez enregistrer les fichiers (script Python et .yml) et modifier les variables avec des noms significatifs pour vous et votre projet."
            })}
          </li>
        </ol>
      </section>

      {/* Assumptions */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.assumptions.title", {
            defaultValue: "Hypothèses"
          })}
        </h2>

        <ol>
          <li>
            {t("helpgh.sections.assumptions.item1", {
              defaultValue: "Que vous possédez déjà un compte utilisateur sur la plateforme WhiteHaX AI-ASM et que vous avez récupéré la clé d'API REST depuis la plateforme."
            })}
          </li>

          <li>
            {t("helpgh.sections.assumptions.item2", {
              defaultValue: "Que vous utiliserez un profil de test et un point de terminaison d'IA créés précédemment, ou que vous en créerez de nouveaux à l'aide de l'API REST WhiteHaX."
            })}
          </li>
        </ol>
      </section>

      {/* What Integration Does */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.whatIntegrationDoes.title", {
            defaultValue: "1. Ce que fait cette intégration"
          })}
        </h2>

        <p>
          {t("helpgh.sections.whatIntegrationDoes.p1", {
            defaultValue: "Ce guide décrit how to run a WhiteHaX DAST verification step after an AWS-backed deployment reports a successful GitHub deployment status."
          })}
        </p>

        <p>
          {t("helpgh.sections.whatIntegrationDoes.p2", {
            defaultValue: "Le flux de travail GitHub Actions reçoit l'URL de l'environnement déployé, appelle un script passerelle Python et téléverse le rapport SARIF généré dans l'analyse de code GitHub."
          })}
        </p>

        <div className="info-box">
          <strong>
            {t("helpgh.sections.whatIntegrationDoes.infoBox.title", {
              defaultValue: "Remarque : Remarque sur la cible de déploiement"
            })}
          </strong>

          <p>
            {t("helpgh.sections.whatIntegrationDoes.infoBox.p1", {
              defaultValue: "La vérification du statut de déploiement AWS s'applique uniquement lorsque le serveur d'exécution de l'application est réellement déployé dans AWS et que le processus de déploiement publie un événement deployment_status GitHub avec une URL environment_url accessible."
            })}
          </p>

          <p>
            {t("helpgh.sections.whatIntegrationDoes.infoBox.p2", {
              defaultValue: "Si la cible d'exécution est locale, sur le poste de travail d'un développeur ou au sein d'un réseau privé, le flux de travail doit transmettre une variable TARGET_URL explicite et le script Python doit gérer ce mode en conséquence."
            })}
          </p>

          <p>
            {t("helpgh.sections.whatIntegrationDoes.infoBox.p3", {
              defaultValue: "Pour les exécuteurs hébergés par GitHub, localhost désigne l'exécuteur lui-même et non la machine du développeur. Utilisez un exécuteur auto-hébergé, un point de terminaison accessible via VPN, une URL de staging ou un tunnel contrôlé lorsqu'une analyse d'exécution locale/privée est requise."
            })}
          </p>
        </div>

        <h3>
          {t("helpgh.sections.whatIntegrationDoes.requirementsTitle", {
            defaultValue: "Exigences"
          })}
        </h3>

        <ul className="bullet-list">
          <li>
            {t("helpgh.sections.whatIntegrationDoes.req1", {
              defaultValue: "Le système de déploiement doit créer des événements deployment_status dans le dépôt GitHub."
            })}
          </li>

          <li>
            {t("helpgh.sections.whatIntegrationDoes.req2", {
              defaultValue: "Le statut de déploiement réussi doit inclure environment_url afin que l'analyse cible le point de terminaison d'exécution en direct."
            })}
          </li>

          <li>
            {t("helpgh.sections.whatIntegrationDoes.req3", {
              defaultValue: "L'analyse de code GitHub doit être activée sur le dépôt pour que le téléversement SARIF apparaisse sous forme d'alertes."
            })}
          </li>

          <li>
            {t("helpgh.sections.whatIntegrationDoes.req4", {
              defaultValue: "Le script Python doit écrire le fichier whitehax_results.sarif à la racine de l'espace de travail du dépôt."
            })}
          </li>
        </ul>
      </section>

      {/* Repository Layout & Usage Guide */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.repositoryLayout.title", {
            defaultValue: "2. Utilisation de ce dépôt sur GitHub"
          })}
        </h2>

        <p>
          {t("helpgh.sections.repositoryLayout.intro", {
            defaultValue: "Voici ce qu'un développeur doit faire pour utiliser ce dépôt sur son compte GitHub :"
          })}
        </p>

        <ol>
          <li>
            <strong>{t("helpgh.sections.repositoryLayout.step1Title", { defaultValue: "Obtenir le code : " })}</strong>
            {t("helpgh.sections.repositoryLayout.step1Text", { defaultValue: "Forkez ou clonez le dépôt dans votre compte GitHub et conservez .github/workflows/whitehax-dast.yml ainsi que whitehax_scans/." })}
          </li>
          <li>
            <strong>{t("helpgh.sections.repositoryLayout.step2Title", { defaultValue: "Activer les fonctionnalités GitHub : " })}</strong>
            {t("helpgh.sections.repositoryLayout.step2Text", { defaultValue: "Activez les Actions pour le dépôt et l'analyse de code (Security → Code scanning) pour permettre le téléchargement SARIF." })}
          </li>



     <img style={{width:'65%'}}
  src={t("helpgh.header.image")}

/>
<br/><br/>

          <li>
            <strong>{t("helpgh.sections.repositoryLayout.step3Title", { defaultValue: "Ajouter les secrets du dépôt : " })}</strong>
            {t("helpgh.sections.repositoryLayout.step3Text", { defaultValue: "Accédez à Repo → Settings → Secrets and variables → Actions pour configurer les secrets requis." })}
          </li>


     <img style={{width:'65%'}}
  src={t("helpgh.header.image2")}

/>
<br/>
<br/>
        </ol>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{t("helpgh.sections.repositoryLayout.table.secret", { defaultValue: "Secret" })}</th>
                <th>{t("helpgh.sections.repositoryLayout.table.purpose", { defaultValue: "Objectif" })}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>WHITEHAX_URL</code></td>
                <td>{t("helpgh.sections.repositoryLayout.table.s1", { defaultValue: "ex. https://demo.whitehax.com" })}</td>
              </tr>
              <tr>
                <td><code>WHITEHAX_API_KEY</code></td>
                <td>{t("helpgh.sections.repositoryLayout.table.s2", { defaultValue: "Votre clé API WhiteHax" })}</td>
              </tr>
              <tr>
                <td><code>WHITEHAX_EMAIL</code></td>
                <td>{t("helpgh.sections.repositoryLayout.table.s3", { defaultValue: "Email de connexion WhiteHax" })}</td>
              </tr>
              <tr>
                <td><code>WHITEHAX_PASSWORD</code></td>
                <td>{t("helpgh.sections.repositoryLayout.table.s4", { defaultValue: "Mot de passe WhiteHax" })}</td>
              </tr>
              <tr>
                <td><code>WHITEHAX_PROFILE_ID</code></td>
                <td>{t("helpgh.sections.repositoryLayout.table.s5", { defaultValue: "ID de profil WhiteHax existant (ex. 418)" })}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="guide-note" style={{ marginTop: "12px" }}>
          {t("helpgh.sections.repositoryLayout.optionalNote", {
            defaultValue: "Optionnel : WHITEHAX_CREATE_RESOURCES si vous souhaitez que le flux de travail crée un point de terminaison/profil au lieu d'en réutiliser un."
          })}
        </p>

        <ol start="4">
          <li>
            <strong>{t("helpgh.sections.repositoryLayout.step4Title", { defaultValue: "Disposer d'un profil WhiteHax utilisable : " })}</strong>
            {t("helpgh.sections.repositoryLayout.step4Text", { defaultValue: "Le compte doit fonctionner sur WhiteHax, le profil doit déjà exister (ou utiliser le mode de création), et son point de terminaison doit correspondre à l'URL cible." })}
          </li>
          <li>
            <strong>{t("helpgh.sections.repositoryLayout.step5Title", { defaultValue: "Lancer l'analyse : " })}</strong>
            {t("helpgh.sections.repositoryLayout.step5Text", { defaultValue: "Manuellement via Actions, automatiquement après un déploiement réussi publiant deployment_status avec environment_url, ou via la CLI GitHub (gh workflow run)." })}
          </li>
          <li>
            <strong>{t("helpgh.sections.repositoryLayout.step6Title", { defaultValue: "Vérifier les résultats : " })}</strong>
            <br/>
            {t("helpgh.sections.repositoryLayout.step6Text1")}<br/>
            <img style={{width:'65%'}}
  src={t("helpgh.header.image3")}

/><br/>
            {t("helpgh.sections.repositoryLayout.step6Text2")}<br/>
            {t("helpgh.sections.repositoryLayout.step6Text3")}<br/>
          </li>
     <img style={{width:'65%'}}
  src={t("helpgh.header.image4")}

/>


        </ol>

        <div className="info-box">
          <strong>{t("helpgh.sections.repositoryLayout.infoBoxTitle", { defaultValue: "À veiller également :" })}</strong>
          <p>{t("helpgh.sections.repositoryLayout.infoBoxP1", { defaultValue: "Ne commitez pas de vrais mots de passe ou clés API dans le code ou dans input_*.json." })}</p>
          <p>{t("helpgh.sections.repositoryLayout.infoBoxP2", { defaultValue: "L'URL cible doit être accessible depuis les scanners de WhiteHax (URL publique ou de staging, pas localhost sur un ordinateur portable)." })}</p>
          <p>{t("helpgh.sections.repositoryLayout.infoBoxP3", { defaultValue: "Remarque : Le flux de travail n'est pas déclenché par un simple git push. Seule une exécution manuelle ou un événement de succès de déploiement le démarre." })}</p>
        </div>
      </section>

      {/* GitHub Actions */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.githubActions.title", {
            defaultValue: "3. Flux de travail GitHub Actions recommandé"
          })}
        </h2>

        <p>
          {t("helpgh.sections.githubActions.p1", {
            defaultValue: "Enregistrez le YAML suivant sous le nom .github/workflows/whitehax-dast.yml."
          })}
        </p>

        <p>
          {t("helpgh.sections.githubActions.p2", {
            defaultValue: "Le flux de travail filtre les déploiements réussis, exige une URL d'environnement non vide, transmet les secrets via des variables d'environnement et ne téléverse le fichier SARIF que lorsqu'il existe."
          })}
        </p>

        <pre className="code-block">{`name: WhiteHax DAST Verification

on:
  deployment_status:

jobs:
  whitehax_dast_scan:
    name: WhiteHax DAST Scan

    if: >-
      github.event.deployment_status.state == 'success' &&
      github.event.deployment_status.environment_url != ''

    runs-on: ubuntu-latest

    permissions:
      contents: read
      security-events: write

    steps:

      - name: Checkout repository code
        uses: actions/checkout@v4

      - name: Set up Python
        uses: actions/setup-python@v5
        with:
          python-version: '3.11'
          cache: 'pip'

      - name: Install WhiteHax scan dependencies
        run: |
          python -m pip install --upgrade pip

          if [ -f whitehax_scans/requirements.txt ]; then
            pip install -r whitehax_scans/requirements.txt
          else
            pip install requests
          fi

      - name: Execute WhiteHax DAST security automation
        env:
          WHITEHAX_URL: \${{ secrets.WHITEHAX_URL }}
          WHITEHAX_API_KEY: \${{ secrets.WHITEHAX_API_KEY }}
          TARGET_URL: \${{ github.event.deployment_status.environment_url }}
          WHITEHAX_FAIL_ON_ERROR: 'false'

        run: |
          echo "Starting WhiteHax DAST verification"
          echo "Target URL: $TARGET_URL"
          python whitehax_scans/whitehax_tests.py

      - name: Upload WhiteHax findings to GitHub code scanning
        if: always() && hashFiles('whitehax_results.sarif') != ''

        uses: github/codeql-action/upload-sarif@v3

        with:
          sarif_file: whitehax_results.sarif
          category: whitehax-dast`}</pre>
      </section>

      {/* Python Bridge */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.pythonBridge.title", {
            defaultValue: "4. Passerelle SARIF Python recommandée"
          })}
        </h2>

        <p>
          {t("helpgh.sections.pythonBridge.p1", {
            defaultValue: "Enregistrez le fichier suivant sous le nom whitehax_scans/whitehax_tests.py."
          })}
        </p>

        <div className="warning-box">
          <strong>
            {t("helpgh.sections.pythonBridge.warningBox.title", {
              defaultValue: "Important"
            })}
          </strong>

          <p>
            {t("helpgh.sections.pythonBridge.warningBox.description", {
              defaultValue: "La section d'appel à l'API REST est intentionnellement laissée sous forme de TODO car le point de terminaison WhiteHaX exact, la charge utile de requête, le comportement de sondage et les champs de réponse sont spécifiques à la plateforme."
            })}
          </p>
        </div>

        <pre className="code-block">{`#!/usr/bin/env python3

\"\"\"
Passerelle SARIF GitHub Actions pour WhiteHax.

Cet exemple est intentionnellement divisé en deux parties :

1. une gestion CI/SARIF stable qui peut être réutilisée telle quelle, et
2. un adaptateur d'API REST WhiteHax spécifique au projet qui doit être complété
   avec le contrat de point de terminaison WhiteHax réel, la charge utile de requête
   et le schéma de réponse.
\"\"\"

import json
import os
import sys
import traceback

from datetime import datetime, timezone
from pathlib import Path
from urllib.parse import urljoin

import requests


OUTPUT_FILE = Path("whitehax_results.sarif")

SARIF_LEVELS = {
    "critical": "error",
    "high": "error",
    "error": "error",
    "medium": "warning",
    "warning": "warning",
    "low": "note",
    "info": "note",
    "informational": "note",
    "none": "none",
}


def require_env(name):
    value = os.environ.get(name)

    if not value:
        raise RuntimeError(
            f"Variable d'environnement requise manquante : {name}"
        )

    return value


def sarif_level(severity):
    return SARIF_LEVELS.get(
        str(severity or "").strip().lower(),
        "warning"
    )


def normalize_finding(raw):
    \"\"\"Associe une détection de l'API WhiteHax à la structure interne ci-dessous.\"\"\"

    return {
        "id": str(
            raw.get("id")
            or raw.get("rule_id")
            or "WHX-GENERIC"
        ),

        "title": str(
            raw.get("title")
            or raw.get("name")
            or "Détection WhiteHax"
        ),

        "description": str(
            raw.get("description")
            or raw.get("message")
            or ""
        ),

        "severity": sarif_level(
            raw.get("severity")
        ),

        "uri": str(
            raw.get("uri")
            or raw.get("path")
            or ""
        ),
    }


def run_whitehax_scan(
    whitehax_url,
    api_key,
    target_url
):
    \"\"\"
    TODO : Remplacez cet espace réservé par le flux
    de travail réel de l'API REST WhiteHax.

    Comportement attendu :

    - s'authentifier avec WHITEHAX_API_KEY sans l'afficher
    - soumettre ou déclencher une analyse pour TARGET_URL
    - sonder jusqu'à ce que l'analyse soit terminée ou échoue
    - récupérer les détections de l'analyse terminée
    - renvoyer une liste de dictionnaires que
      normalize_finding() peut consommer.
    \"\"\"

    raise NotImplementedError(
        "L'adaptateur d'API REST WhiteHax est spécifique au projet."
    )


def build_sarif(
    findings,
    target_url,
    execution_error=None
):

    run = {
        "tool": {
            "driver": {
                "name": "WhiteHax DAST",
                "informationUri": "https://whitehax.com",
                "rules": [],
            }
        },

        "results": [],

        "invocations": [
            {
                "executionSuccessful":
                    execution_error is None,

                "endTimeUtc":
                    datetime.now(
                        timezone.utc
                    ).isoformat(),
            }
        ],
    }

    rules = {}

    for raw in findings:

        finding = normalize_finding(raw)

        if finding["id"] not in rules:

            rules[finding["id"]] = {
                "id": finding["id"],

                "name": finding["title"],

                "shortDescription": {
                    "text": finding["title"]
                },

                "fullDescription": {
                    "text":
                        finding["description"]
                        or finding["title"]
                },

                "defaultConfiguration": {
                    "level":
                        finding["severity"]
                },
            }

        location_uri = urljoin(
            target_url.rstrip("/") + "/",
            finding["uri"].lstrip("/")
        )

        run["results"].append(
            {
                "ruleId": finding["id"],

                "level": finding["severity"],

                "message": {
                    "text":
                        finding["description"]
                        or finding["title"]
                },

                "locations": [
                    {
                        "physicalLocation": {
                            "artifactLocation": {
                                "uri": location_uri
                            }
                        }
                    }
                ],
            }
        )

    if execution_error:

        rules["WHX-SCAN-ERROR"] = {
            "id": "WHX-SCAN-ERROR",

            "name":
                "Erreur d'exécution de l'analyse WhiteHax",

            "shortDescription": {
                "text":
                    "Erreur d'exécution de l'analyse WhiteHax"
            },

            "defaultConfiguration": {
                "level": "warning"
            },
        }

        run["results"].append(
            {
                "ruleId": "WHX-SCAN-ERROR",

                "level": "warning",

                "message": {
                    "text":
                        execution_error[:4000]
                },

                "locations": [
                    {
                        "physicalLocation": {
                            "artifactLocation": {
                                "uri": target_url
                            }
                        }
                    }
                ],
            }
        )

    run["tool"]["driver"]["rules"] = list(
        rules.values()
    )

    return {
        "$schema":
            "https://json.schemastore.org/sarif-2.1.0.json",

        "version": "2.1.0",

        "runs": [run],
    }


def write_sarif(payload):

    OUTPUT_FILE.write_text(
        json.dumps(payload, indent=2),
        encoding="utf-8"
    )

    print(
        f"[INFO] Résultats SARIF écrits dans {OUTPUT_FILE}"
    )


def main():

    target_url = os.environ.get(
        "TARGET_URL",
        ""
    )

    try:

        whitehax_url = require_env(
            "WHITEHAX_URL"
        )

        api_key = require_env(
            "WHITEHAX_API_KEY"
        )

        target_url = require_env(
            "TARGET_URL"
        )

        findings = run_whitehax_scan(
            whitehax_url,
            api_key,
            target_url
        )

        write_sarif(
            build_sarif(
                findings,
                target_url
            )
        )

    except Exception as exc:

        print(
            "[ERROR] L'analyse WhiteHax a échoué :",
            exc,
            file=sys.stderr
        )

        traceback.print_exc()

        write_sarif(
            build_sarif(
                [],
                target_url or "about:blank",
                str(exc)
            )
        )

        if os.environ.get(
            "WHITEHAX_FAIL_ON_ERROR",
            "false"
        ).lower() == "true":

            return 1

    return 0


if __name__ == "__main__":
    raise SystemExit(main())`}</pre>
      </section>

      {/* Validation Notes */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.validationNotes.title", {
            defaultValue: "5. Notes de validation"
          })}
        </h2>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>
                  {t("helpgh.sections.validationNotes.table.headers.area", {
                    defaultValue: "Domaine"
                  })}
                </th>
                <th>
                  {t("helpgh.sections.validationNotes.table.headers.status", {
                    defaultValue: "Statut"
                  })}
                </th>
                <th>
                  {t("helpgh.sections.validationNotes.table.headers.recommendedHandling", {
                    defaultValue: "Gestion recommandée"
                  })}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.workflowTrigger.area", {
                    defaultValue: "Déclencheur de flux de travail"
                  })}
                </td>
                <td>
                  <span className="status valid">
                    {t("helpgh.sections.validationNotes.table.rows.workflowTrigger.status", {
                      defaultValue: "Valide"
                    })}
                  </span>
                </td>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.workflowTrigger.handling", {
                    defaultValue: "Un statut deployment_status valide est un déclencheur GitHub Actions pris en charge. Le flux de travail doit filtrer sur state == success et exiger environment_url."
                  })}
                </td>
              </tr>

              <tr>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.secretsHandling.area", {
                    defaultValue: "Gestion des secrets"
                  })}
                </td>
                <td>
                  <span className="status guardrails">
                    {t("helpgh.sections.validationNotes.table.rows.secretsHandling.status", {
                      defaultValue: "Valide avec garde-fous"
                    })}
                  </span>
                </td>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.secretsHandling.handling", {
                    defaultValue: "WHITEHAX_URL et WHITEHAX_API_KEY doivent être transmis sous forme de variables d'environnement et ne jamais être affichés."
                  })}
                </td>
              </tr>

              <tr>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.restApiAdapter.area", {
                    defaultValue: "Adaptateur d'API REST"
                  })}
                </td>
                <td>
                  <span className="status project">
                    {t("helpgh.sections.validationNotes.table.rows.restApiAdapter.status", {
                      defaultValue: "Spécifique au projet"
                    })}
                  </span>
                </td>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.restApiAdapter.handling", {
                    defaultValue: "Conservez ceci comme un espace réservé jusqu'à ce que le point de terminaison WhiteHax, la méthode d'authentification, la charge utile de requête et le schéma de réponse soient confirmés."
                  })}
                </td>
              </tr>

              <tr>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.sarifUpload.area", {
                    defaultValue: "Téléversement SARIF"
                  })}
                </td>
                <td>
                  <span className="status valid">
                    {t("helpgh.sections.validationNotes.table.rows.sarifUpload.status", {
                      defaultValue: "Valide"
                    })}
                  </span>
                </td>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.sarifUpload.handling", {
                    defaultValue: "Utilisez github/codeql-action/upload-sarif@v3 avec security-events: write et contents: read."
                  })}
                </td>
              </tr>

              <tr>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.sarifGeneration.area", {
                    defaultValue: "Génération SARIF"
                  })}
                </td>
                <td>
                  <span className="status hardening">
                    {t("helpgh.sections.validationNotes.table.rows.sarifGeneration.status", {
                      defaultValue: "Nécessite un renforcement"
                    })}
                  </span>
                </td>
                <td>
                  {t("helpgh.sections.validationNotes.table.rows.sarifGeneration.handling", {
                    defaultValue: "Dédupliquez les règles, associez les sévérités aux niveaux SARIF et écrivez toujours un fichier SARIF, même en cas d'échec de l'adaptateur d'analyse."
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Implementation Checklist */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.implementationChecklist.title", {
            defaultValue: "6. Liste de contrôle de mise en œuvre"
          })}
        </h2>

        <ul className="bullet-list">
          <li>
            {t("helpgh.sections.implementationChecklist.item1", {
              defaultValue: "Créez les secrets de dépôt WHITEHAX_URL et WHITEHAX_API_KEY."
            })}
          </li>
          <li>
            {t("helpgh.sections.implementationChecklist.item2", {
              defaultValue: "Confirmez que le producteur de déploiement définit deployment_status.state sur success et inclut environment_url."
            })}
          </li>
          <li>
            {t("helpgh.sections.implementationChecklist.item3", {
              defaultValue: "Remplacez run_whitehax_scan() par le flux réel d'envoi, de sondage et de récupération des résultats de l'API REST WhiteHaX."
            })}
          </li>
          <li>
            {t("helpgh.sections.implementationChecklist.item4", {
              defaultValue: "Mappez les champs de détection réels de WhiteHaX vers id, title, description, severity et uri."
            })}
          </li>
          <li>
            {t("helpgh.sections.implementationChecklist.item5", {
              defaultValue: "Exécutez le flux de travail dans un dépôt de staging et confirmez que whitehax_results.sarif se téléverse avec succès."
            })}
          </li>
          <li>
            {t("helpgh.sections.implementationChecklist.item6", {
              defaultValue: "Décidez si WHITEHAX_FAIL_ON_ERROR doit rester à false pour le mode rapport uniquement ou passer à true pour le mode bloquant."
            })}
          </li>
        </ul>
      </section>

      {/* Common Pitfalls */}
      <section className="guide-section">
        <h2>
          {t("helpgh.sections.commonPitfalls.title", {
            defaultValue: "7. Pièges courants"
          })}
        </h2>

        <ul className="bullet-list">
          <li>
            {t("helpgh.sections.commonPitfalls.pitfall1", {
              defaultValue: "Ne pas afficher les clés d'API, les jetons porteurs ou les en-têtes de requête complets dans les journaux Actions."
            })}
          </li>

          <li>
            {t("helpgh.sections.commonPitfalls.pitfall2", {
              defaultValue: "Ne pas vous fier uniquement à if: always() ; le script doit toujours créer un fichier SARIF lorsque l'adaptateur d'analyse échoue."
            })}
          </li>

          <li>
            {t("helpgh.sections.commonPitfalls.pitfall3", {
              defaultValue: "Ne pas transmettre les valeurs de sévérité brutes de WhiteHaX directement dans SARIF, sauf si elles correspondent déjà à error, warning, note ou none."
            })}
          </li>

          <li>
            {t("helpgh.sections.commonPitfalls.pitfall4", {
              defaultValue: "Ne pas créer d'entrées de règle SARIF en double pour les détections répétées ayant le même identifiant de règle."
            })}
          </li>
        </ul>

        <div className="info-box final-note">
          {t("helpgh.sections.commonPitfalls.finalNote", {
            defaultValue: "Pour une utilisation en déploiement réel, validez le fichier SARIF final par rapport à l'analyse de code GitHub dans un dépôt de staging avant d'activer le comportement bloquant en production."
          })}
        </div>
      </section>

    </div>
  );
}

/* =========================================================
   INTERNAL CSS
   ========================================================= */

const styles = `
  .whx-guide {
    min-height: 100vh;
    background: #0F172A;
    color: #E5E7EB;
    padding: 40px 50px 80px;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      Arial,
      sans-serif;
    line-height: 1.65;
  }

  .guide-header {
    max-width: 1100px;
    margin: 0 auto 35px;
    padding-bottom: 25px;
    border-bottom: 1px solid #334155;
  }

  .guide-header h1 {
    margin: 0 0 10px;
    color: #F8FAFC;
    font-size: 30px;
    font-weight: 700;
  }

  .guide-header p {
    margin: 0;
    color: #94A3B8;
    font-size: 15px;
  }

  .guide-section {
    max-width: 1100px;
    margin: 0 auto 32px;
    padding: 28px;
    background: #111827;
    border: 1px solid #263449;
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.18);
  }

  .guide-section h2 {
    margin: 0 0 18px;
    color: #F8FAFC;
    font-size: 22px;
  }

  .guide-section h3 {
    margin: 25px 0 12px;
    color: #CBD5E1;
    font-size: 17px;
  }

  .guide-section p {
    color: #CBD5E1;
    font-size: 14px;
  }

  .guide-section ol,
  .guide-section ul {
    color: #CBD5E1;
    font-size: 14px;
  }

  .guide-section li {
    margin-bottom: 10px;
  }

  .whx-guide code {
    padding: 2px 6px;
    border-radius: 4px;
    background: #020617;
    color: #93C5FD;
    font-family: Consolas, monospace;
  }

  .info-box {
    margin: 22px 0;
    padding: 18px 20px;
    background: #172554;
    border-left: 4px solid #3B82F6;
    border-radius: 7px;
  }

  .info-box strong {
    color: #93C5FD;
  }

  .info-box p {
    margin: 8px 0 0;
  }

  .warning-box {
    margin: 20px 0;
    padding: 18px 20px;
    background: #3F2A05;
    border-left: 4px solid #F59E0B;
    border-radius: 7px;
  }

  .warning-box strong {
    color: #FBBF24;
  }

  .warning-box p {
    margin-bottom: 0;
  }

  .bullet-list {
    padding-left: 25px;
  }

  .image-omitted {
    max-width: 1100px;
    margin: 0 auto 32px;
    padding: 14px;
    text-align: center;
    color: #64748B;
    background: transparent;
    border: 1px dashed #334155;
    border-radius: 8px;
    font-size: 12px;
  }

  .code-block {
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto;
    padding: 20px;
    margin: 18px 0;
    background: #020617;
    color: #CBD5E1;
    border: 1px solid #1E293B;
    border-radius: 8px;
    font-family: Consolas, "Courier New", monospace;
    font-size: 13px;
    line-height: 1.6;
    white-space: pre;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .whx-guide table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .whx-guide th {
    padding: 14px;
    text-align: left;
    background: #1E293B;
    color: #F8FAFC;
    border: 1px solid #334155;
  }

  .whx-guide td {
    padding: 14px;
    vertical-align: top;
    color: #CBD5E1;
    border: 1px solid #334155;
  }

  .whx-guide tr:nth-child(even) td {
    background: #0F172A;
  }

  .status {
    display: inline-block;
    padding: 4px 9px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .status.valid {
    background: #064E3B;
    color: #6EE7B7;
  }

  .status.guardrails {
    background: #422006;
    color: #FCD34D;
  }

  .status.project {
    background: #312E81;
    color: #C4B5FD;
  }

  .status.hardening {
    background: #4C0519;
    color: #FDA4AF;
  }

  .checklist {
    display: flex;
    flex-direction: column;
    gap: 13px;
  }

  .checklist label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: #0F172A;
    border: 1px solid #263449;
    border-radius: 7px;
    color: #CBD5E1;
    font-size: 14px;
    cursor: pointer;
  }

  .checklist input {
    margin-top: 5px;
    accent-color: #3B82F6;
  }

  .final-note {
    margin-top: 25px;
  }

  @media (max-width: 768px) {
    .whx-guide {
      padding: 20px 15px 50px;
    }

    .guide-section {
      padding: 20px;
    }

    .guide-header h1 {
      font-size: 24px;
    }

    .guide-section h2 {
      font-size: 19px;
    }

    .code-block {
      font-size: 11px;
      padding: 14px;
    }
  }
`;

// Inject internal CSS
if (typeof document !== "undefined") {
  const styleId = "whitehax-cicd-guide-styles";

  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = styles;
    document.head.appendChild(style);
  }
}